"use client";

import React, { useState } from "react";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information";
import { useTradeHistoryStore } from "@/vybe-trader-client/trade-history/stores/useTradeHistoryStore";
import { formatHistorySyncSource } from "@/vybe-trader-client/trade-history/types";
import type { AccountAnalytics } from "@/vybe-trader-client/trade-history/accountAnalytics";
import { formatHistoryLocalDateTime } from "@/components/history-view/historyDateTimeFormat";
import {
  AnalyticsCard,
  AnalyticsCardHeader,
  AnalyticsTab,
  StatRow,
  fmtDayMonth,
  fmtMoney,
  fmtSignedMoney,
  fmtSignedPct,
  fmtPct,
  pnlClass,
} from "./analyticsShared";

type InfoTab = "stats" | "general";

/**
 * Info card — Stats + General tabs. Numbers cover ONLY the synced window
 * shown in the modal's scope chips (labelled honestly per the proposal).
 */
export default function AnalyticsInfoCard({
  analytics,
}: {
  analytics: AccountAnalytics;
}) {
  const [tab, setTab] = useState<InfoTab>("stats");
  const account = useAccountInformationStore((s) => s.account);
  const positionsSynced = useTradeHistoryStore((s) => s.allPositions.length);
  const dealsSynced = useTradeHistoryStore((s) => s.allDeals.length);
  const newestPosition = useTradeHistoryStore((s) => s.allPositions[0]);

  const st = analytics.stats;
  const currency = account?.currency || "";

  return (
    <AnalyticsCard className="flex flex-col">
      <AnalyticsCardHeader>
        <span className="mr-1">Info</span>
        <AnalyticsTab active={tab === "stats"} onClick={() => setTab("stats")}>
          Stats
        </AnalyticsTab>
        <AnalyticsTab
          active={tab === "general"}
          onClick={() => setTab("general")}
        >
          General
        </AnalyticsTab>
      </AnalyticsCardHeader>

      <div className="p-2 flex-1">
        {tab === "stats" ? (
          <div>
            <StatRow
              label="Gain (window)"
              value={fmtSignedPct(st.gainPct)}
              valueClass={pnlClass(st.gainPct)}
            />
            <StatRow label="Daily" value={fmtSignedPct(st.dailyPct)} />
            <StatRow label="Monthly" value={fmtSignedPct(st.monthlyPct)} />
            <StatRow
              label="Drawdown (balance)"
              value={fmtPct(st.drawdownPct)}
              valueClass={
                st.drawdownPct !== null && st.drawdownPct > 0
                  ? "text-red-400"
                  : undefined
              }
            />
            <StatRow
              label="Balance"
              value={fmtMoney(account?.balance ?? st.endBalance, currency)}
            />
            <StatRow
              label="Highest (window)"
              value={fmtMoney(st.highestBalance, currency)}
            />
            <StatRow
              label="Profit (window)"
              value={fmtSignedMoney(st.netProfit, currency)}
              valueClass={pnlClass(st.netProfit)}
            />
            <StatRow label="Deposits" value={fmtMoney(st.deposits, currency)} />
            <StatRow
              label="Withdrawals"
              value={fmtMoney(st.withdrawals, currency)}
            />
            <StatRow
              label="First trade"
              value={fmtDayMonth(analytics.firstTradeMs)}
            />
            <StatRow
              label="Last trade"
              value={fmtDayMonth(analytics.lastTradeMs)}
            />
          </div>
        ) : (
          <div>
            <StatRow label="Broker" value={account?.companyName || "—"} />
            <StatRow label="Server" value={account?.serverName || "—"} />
            <StatRow
              label="Account type"
              value={
                [account?.marginMode, account?.type]
                  .filter(Boolean)
                  .join(" · ") || "—"
              }
            />
            <StatRow label="Currency" value={currency || "—"} />
            <StatRow
              label="Leverage"
              value={account?.leverage ? `1:${account.leverage}` : "—"}
            />
            <StatRow label="Positions synced" value={positionsSynced} />
            <StatRow label="Deals synced" value={dealsSynced} />
            <StatRow
              label="Sync source"
              value={formatHistorySyncSource(newestPosition?.syncSource)}
            />
            <StatRow
              label="Synced at"
              value={
                newestPosition?.syncedAt
                  ? formatHistoryLocalDateTime(newestPosition.syncedAt)
                  : "—"
              }
            />
          </div>
        )}
      </div>
    </AnalyticsCard>
  );
}
